import { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useNavigate, useParams } from "react-router-dom";
import SinglePage from "../SinglePage/SinglePage";
import SubmitButton from "../../base/Buttons/SubmitButton/SubmitButton";
import ProductPlaceholder from "../../../assets/images/shop/product-placeholder.svg";
import ShopNavbar from "./ShopNavbar";
import ShopActionFeedback from "./ShopActionFeedback/ShopActionFeedback";
import ConfirmationDialog from "../../base/ConfirmationDialog";
import {
  deleteUsedShopProduct,
  expressInterestInUsedShopProduct,
  getShopCatalog,
  ShopProductDto,
  ShopProductOptionDto,
} from "../../../services/shopApi";
import { AUTH_CHANGED_EVENT, loadAuthSession, requestLoginDialog } from "../../../services/authStorage";
import { getProductImagesWithFallback, resolveShopImageSrc } from "./shopImageUtils";
import {
  ActionRow,
  CartHeader,
  CartItem,
  CartItemHeader,
  CartItemName,
  CartItemOptions,
  CartItemPrice,
  CartList,
  CartSlider,
  CartSummary,
  CheckoutHint,
  CartTitle,
  CarouselArrow,
  Category,
  CloseCartButton,
  Description,
  DetailContent,
  DetailImage,
  DetailEmblaContainer,
  DetailEmblaSlide,
  DetailEmblaViewport,
  DetailLayout,
  ImagePanel,
  ImagePlaceholder,
  InfoPanel,
  MetaRow,
  Name,
  OptionGroup,
  OptionLabel,
  OptionSelect,
  OptionSelectorList,
  OptionTextInput,
  OptionValidationText,
  Overlay,
  Price,
  QuantityButton,
  QuantityRow,
  QuantityValue,
  RemoveButton,
  SummaryRow,
  ThumbButton,
  ThumbImage,
  ThumbPlaceholder,
  ThumbRow,
  EmptyText,
  FaqAnswer,
  FaqCard,
  FaqHeading,
  FaqQuestion,
  FaqSection,
} from "./productDetailStyles";

interface CartEntry {
  key: string;
  productSlug: string;
  productName: string;
  price: number;
  quantity: number;
  selectedOptions: Record<string, string>;
}

const CART_STORAGE_KEY = "shop-cart-entries";

const formatPrice = (value: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const formatOptions = (options: Record<string, string>): string => {
  const entries = Object.entries(options);
  return entries.length === 0 ? "Standard" : entries.map(([name, value]) => `${name}: ${value}`).join(" | ");
};

const buildCartKey = (productSlug: string, selectedOptions: Record<string, string>): string =>
  `${productSlug}|${Object.entries(selectedOptions)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([name, value]) => `${name}:${value}`)
    .join("|")}`;

const getProductImages = (product: ShopProductDto): string[] =>
  getProductImagesWithFallback(product.slug, product.imageUrls);

const buildDefaultSelection = (product: ShopProductDto): Record<string, string> =>
  product.options.reduce<Record<string, string>>((acc, option) => {
    if (option.values.length > 0) {
      acc[option.name] = option.values[0];
    }
    return acc;
  }, {});

const isStandaloneCustomOption = (option: ShopProductOptionDto): boolean =>
  option.allowsCustomValue && Boolean(option.customValueKey) && option.values.length === 0;

const buildSelectionWithCustomValues = (
  product: ShopProductDto,
  selection: Record<string, string>,
  customValues: Record<string, string>
) => {
  const normalizedSelection = { ...selection };

  product.options.forEach((option) => {
    if (!option.allowsCustomValue || !option.customValueKey) {
      return;
    }

    delete normalizedSelection[option.customValueKey];

    const requiresCustomValue =
      isStandaloneCustomOption(option) || selection[option.name] === option.customValueTrigger;

    if (requiresCustomValue && customValues[option.customValueKey]?.trim()) {
      normalizedSelection[option.customValueKey] = customValues[option.customValueKey].trim();
    }
  });

  return normalizedSelection;
};

const validateCustomValues = (
  product: ShopProductDto,
  selection: Record<string, string>,
  customValues: Record<string, string>
) => {
  const errors: Record<string, string> = {};

  product.options.forEach((option) => {
    if (!option.allowsCustomValue || !option.customValueKey || !option.customValueLabel) {
      return;
    }

    if (!isStandaloneCustomOption(option) && selection[option.name] !== option.customValueTrigger) {
      return;
    }

    if (!customValues[option.customValueKey]?.trim()) {
      errors[option.customValueKey] = `Bitte ${option.customValueLabel.toLowerCase()} eingeben.`;
    }
  });

  return errors;
};

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(loadAuthSession());
  const [products, setProducts] = useState<ShopProductDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [customErrors, setCustomErrors] = useState<Record<string, string>>({});
  const [selectedAmount, setSelectedAmount] = useState(1);
  const [cartEntries, setCartEntries] = useState<CartEntry[]>(() => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? (parsed as CartEntry[]).map((entry) => ({
            ...entry,
            productSlug: entry.productSlug ?? entry.key.split("|")[0] ?? "",
          }))
        : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isInterestDialogOpen, setIsInterestDialogOpen] = useState(false);
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [interestMessage, setInterestMessage] = useState("");
  const [interestTone, setInterestTone] = useState<"success" | "error">("success");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteTone, setDeleteTone] = useState<"success" | "error">("success");
  const isShopManager = session?.user.role === "shopmanager";
  const product = products.find((entry) => entry.slug === productId);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    const sync = () => setSession(loadAuthSession());
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, sync);
  }, []);

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);

    const loadCatalog = async () => {
      try {
        const catalog = await getShopCatalog(session?.token);
        if (!ignore) {
          setProducts(catalog.products);
          setLoadError("");
        }
      } catch (error) {
        if (!ignore) {
          setLoadError(error instanceof Error ? error.message : "Shop-Katalog konnte nicht geladen werden.");
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    void loadCatalog();

    return () => {
      ignore = true;
    };
  }, [session?.token]);

  useEffect(() => {
    if (!product) {
      return;
    }

    setSelectedOptions(buildDefaultSelection(product));
    setCustomValues({});
    setCustomErrors({});
    setSelectedAmount(1);
    setBrokenImages(new Set());
    setInterestMessage("");
    setDeleteMessage("");
  }, [product]);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartEntries));
  }, [cartEntries]);

  useEffect(() => {
    if (!interestMessage.trim()) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setInterestMessage("");
    }, interestTone === "error" ? 6500 : 4200);

    return () => window.clearTimeout(timeout);
  }, [interestMessage, interestTone]);

  useEffect(() => {
    if (!deleteMessage.trim()) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setDeleteMessage("");
    }, deleteTone === "error" ? 6500 : 4200);

    return () => window.clearTimeout(timeout);
  }, [deleteMessage, deleteTone]);

  const onInit = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onInit();
    onSelect();
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  const subtotal = useMemo(
    () => cartEntries.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartEntries]
  );

  const cartItemsCount = useMemo(
    () => cartEntries.reduce((sum, entry) => sum + entry.quantity, 0),
    [cartEntries]
  );

  const addToCart = () => {
    if (!product) {
      return;
    }

    if (!loadAuthSession()) {
      requestLoginDialog();
      return;
    }

    const validationErrors = validateCustomValues(product, selectedOptions, customValues);
    if (Object.keys(validationErrors).length > 0) {
      setCustomErrors(validationErrors);
      return;
    }

    const finalSelection = buildSelectionWithCustomValues(product, selectedOptions, customValues);
    const key = buildCartKey(product.slug, finalSelection);

    setCartEntries((prev) => {
      const existing = prev.find((entry) => entry.key === key);
      if (existing) {
        return prev.map((entry) =>
          entry.key === key ? { ...entry, quantity: entry.quantity + selectedAmount } : entry
        );
      }

      return [
        ...prev,
        {
          key,
          productSlug: product.slug,
          productName: product.name,
          price: product.price,
          quantity: selectedAmount,
          selectedOptions: finalSelection,
        },
      ];
    });

    setIsCartOpen(true);
  };

  const confirmInterest = async () => {
    if (!product) {
      return;
    }

    if (!loadAuthSession()) {
      requestLoginDialog();
      return;
    }

    try {
      setIsSubmittingInterest(true);
      await expressInterestInUsedShopProduct(session?.token ?? "", product.id);
      setProducts((prev) => prev.map((entry) => (entry.id === product.id ? { ...entry, hasExpressedInterest: true } : entry)));
      setInterestTone("success");
      setInterestMessage(`Dein Interesse an "${product.name}" wurde versendet.`);
    } catch (error) {
      setInterestTone("error");
      setInterestMessage(error instanceof Error ? error.message : "Interesse konnte nicht versendet werden.");
    } finally {
      setIsSubmittingInterest(false);
      setIsInterestDialogOpen(false);
    }
  };

  const confirmDelete = async () => {
    if (!product) {
      return;
    }

    try {
      setIsDeletingProduct(true);
      await deleteUsedShopProduct(session?.token ?? "", product.id);
      navigate("/shop/gebrauchte-artikel");
    } catch (error) {
      setDeleteTone("error");
      setDeleteMessage(error instanceof Error ? error.message : "Gebrauchtartikel konnte nicht gelöscht werden.");
    } finally {
      setIsDeletingProduct(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const updateCartQuantity = (key: string, direction: "up" | "down") => {
    setCartEntries((prev) =>
      prev.flatMap((entry) => {
        if (entry.key !== key) {
          return [entry];
        }

        if (direction === "down" && entry.quantity === 1) {
          return [];
        }

        return [{ ...entry, quantity: direction === "up" ? entry.quantity + 1 : entry.quantity - 1 }];
      })
    );
  };

  const removeEntry = (key: string) => {
    setCartEntries((prev) => prev.filter((entry) => entry.key !== key));
  };

  const goToCartReview = () => {
    navigate("/shop/warenkorb");
  };

  if (isLoading) {
    return (
      <SinglePage
        title="Produkt wird geladen"
        heroBanner={<></>}
        topic={null}
        content={<DetailContent>Produkt wird geladen.</DetailContent>}
      />
    );
  }

  if (loadError) {
    return (
      <SinglePage
        title="Shop nicht verf\u00fcgbar"
        heroBanner={<></>}
        topic={null}
        content={<DetailContent>{loadError}</DetailContent>}
      />
    );
  }

  if (!product) {
    return (
      <SinglePage
        title="Produkt nicht gefunden"
        heroBanner={<></>}
        topic={null}
        content={
          <DetailContent>
            <ActionRow>
              <SubmitButton text="Zurück zum Shop" onClick={() => navigate("/shop")} />
            </ActionRow>
          </DetailContent>
        }
      />
    );
  }

  const images = getProductImages(product).map((image) => resolveShopImageSrc(image));
  const detailFaqEntries = product.faq ?? [];
  const detailDescription = product.longDescription ?? product.description;

  return (
    <>
      <SinglePage
        title={product.name}
        heroBanner={<></>}
        topic={null}
        content={
          <DetailContent>
            <ShopNavbar cartItemsCount={cartItemsCount} onCartClick={() => setIsCartOpen(true)} />
            <DetailLayout>
              <ImagePanel>
                <DetailEmblaViewport ref={emblaRef}>
                  <DetailEmblaContainer>
                    {images.map((image, index) => (
                      <DetailEmblaSlide key={`${product.slug}-detail-${index}`}>
                        {image && !brokenImages.has(index) ? (
                          <DetailImage
                            src={image}
                            alt={product.name}
                            onError={() =>
                              setBrokenImages((prev) => {
                                const next = new Set(prev);
                                next.add(index);
                                return next;
                              })
                            }
                          />
                        ) : (
                          <ImagePlaceholder>
                            <img src={ProductPlaceholder} alt="Produkt Platzhalter" />
                          </ImagePlaceholder>
                        )}
                      </DetailEmblaSlide>
                    ))}
                  </DetailEmblaContainer>
                  <CarouselArrow type="button" $direction="left" onClick={() => emblaApi?.scrollPrev()} disabled={!canScrollPrev} aria-label="Vorheriges Bild">
                    <svg viewBox="0 0 532 532" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z" /></svg>
                  </CarouselArrow>
                  <CarouselArrow type="button" $direction="right" onClick={() => emblaApi?.scrollNext()} disabled={!canScrollNext} aria-label="N\u00e4chstes Bild">
                    <svg viewBox="0 0 532 532" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z" /></svg>
                  </CarouselArrow>
                </DetailEmblaViewport>
                <ThumbRow>
                  {scrollSnaps.map((_, index) => (
                    <ThumbButton key={`${product.slug}-thumb-${index}`} type="button" $active={index === selectedIndex} onClick={() => emblaApi?.scrollTo(index)} aria-label={`Bild ${index + 1} anzeigen`}>
                      {images[index] && !brokenImages.has(index) ? (
                        <ThumbImage src={images[index]} alt={`${product.name} Vorschau ${index + 1}`} />
                      ) : (
                        <ThumbPlaceholder>
                          <img src={ProductPlaceholder} alt="Produkt Platzhalter" />
                        </ThumbPlaceholder>
                      )}
                    </ThumbButton>
                  ))}
                </ThumbRow>
              </ImagePanel>

              <InfoPanel>
                <MetaRow>
                  <Category>{product.category}</Category>
                </MetaRow>
                <Name>{product.name}</Name>
                <Description>{detailDescription}</Description>
                {product.isSecondHandArticle && product.sourceName ? <Description>Von: {product.sourceName}</Description> : null}
                {product.isSecondHandArticle && product.size ? <Description>Größe: {product.size}</Description> : null}
                <ShopActionFeedback message={interestMessage} tone={interestTone} stackIndex={0} />
                <ShopActionFeedback message={deleteMessage} tone={deleteTone} stackIndex={1} />
                <Price>{formatPrice(product.price)}</Price>

                {!product.isSecondHandArticle && product.options.length > 0 ? (
                  <OptionSelectorList>
                    {product.options.map((option) => (
                      <OptionGroup key={`${product.slug}-${option.name}`}>
                        {option.values.length > 0 ? (
                          <>
                            <OptionLabel htmlFor={`${product.slug}-${option.name}`}>{option.name}</OptionLabel>
                            <OptionSelect id={`${product.slug}-${option.name}`} value={selectedOptions[option.name] ?? option.values[0]} onChange={(event) => setSelectedOptions((prev) => ({ ...prev, [option.name]: event.target.value }))}>
                              {option.values.map((value) => <option key={value} value={value}>{value}</option>)}
                            </OptionSelect>
                          </>
                        ) : null}
                        {option.allowsCustomValue && option.customValueKey && (isStandaloneCustomOption(option) || selectedOptions[option.name] === option.customValueTrigger) ? (
                          <OptionGroup>
                            <OptionLabel htmlFor={`${product.slug}-${option.customValueKey}`}>{option.customValueLabel}</OptionLabel>
                            <OptionTextInput id={`${product.slug}-${option.customValueKey}`} type="text" maxLength={option.customValueMaxLength ?? 30} value={customValues[option.customValueKey] ?? ""} onChange={(event) => setCustomValues((prev) => ({ ...prev, [option.customValueKey!]: event.target.value }))} placeholder={option.customValuePlaceholder ?? ""} />
                            {customErrors[option.customValueKey] ? <OptionValidationText>{customErrors[option.customValueKey]}</OptionValidationText> : null}
                          </OptionGroup>
                        ) : null}
                      </OptionGroup>
                    ))}
                  </OptionSelectorList>
                ) : null}

                {!product.isSecondHandArticle ? (
                  <QuantityRow>
                    <QuantityButton type="button" onClick={() => setSelectedAmount((prev) => Math.max(1, prev - 1))}>-</QuantityButton>
                    <QuantityValue>{selectedAmount}</QuantityValue>
                    <QuantityButton type="button" onClick={() => setSelectedAmount((prev) => prev + 1)}>+</QuantityButton>
                  </QuantityRow>
                ) : null}

                <ActionRow>
                  {product.isSecondHandArticle && isShopManager ? <SubmitButton text="Artikel löschen" onClick={() => setIsDeleteDialogOpen(true)} /> : null}
                  {product.isSecondHandArticle ? <SubmitButton text={product.hasExpressedInterest ? "Interesse bereits bekundet" : "Interesse bekunden"} onClick={() => setIsInterestDialogOpen(true)} disabled={product.hasExpressedInterest} /> : <SubmitButton text="In den Warenkorb" onClick={addToCart} />}
                  <SubmitButton text="Zurück zum Shop" onClick={() => navigate("/shop")} />
                </ActionRow>
              </InfoPanel>
            </DetailLayout>

            {detailFaqEntries.length > 0 ? <FaqSection><FaqHeading>Fragen und Antworten zu diesem Produkt</FaqHeading>{detailFaqEntries.map((entry) => <FaqCard key={`${product.slug}-${entry.question}`}><FaqQuestion>{entry.question}</FaqQuestion><FaqAnswer>{entry.answer}</FaqAnswer></FaqCard>)}</FaqSection> : null}

            <Overlay type="button" $open={isCartOpen} onClick={() => setIsCartOpen(false)} aria-label="Warenkorb schlie\u00dfen" />
            <CartSlider $open={isCartOpen}>
              <CartHeader><CartTitle>Warenkorb</CartTitle><CloseCartButton type="button" onClick={() => setIsCartOpen(false)}>x</CloseCartButton></CartHeader>
              {cartEntries.length === 0 ? <EmptyText>Dein Warenkorb ist aktuell leer.</EmptyText> : <CartList>{cartEntries.map((entry) => <CartItem key={entry.key}><CartItemHeader><CartItemName>{entry.productName}</CartItemName><CartItemPrice>{formatPrice(entry.price * entry.quantity)}</CartItemPrice></CartItemHeader><CartItemOptions>{formatOptions(entry.selectedOptions)}</CartItemOptions><QuantityRow><QuantityButton type="button" onClick={() => updateCartQuantity(entry.key, "down")}>-</QuantityButton><QuantityValue>{entry.quantity}</QuantityValue><QuantityButton type="button" onClick={() => updateCartQuantity(entry.key, "up")}>+</QuantityButton><RemoveButton type="button" onClick={() => removeEntry(entry.key)}>Entfernen</RemoveButton></QuantityRow></CartItem>)}</CartList>}
              <CartSummary><SummaryRow><span>Positionen</span><span>{cartItemsCount}</span></SummaryRow><SummaryRow><strong>Gesamt</strong><strong>{formatPrice(subtotal)}</strong></SummaryRow><CheckoutHint>Pr\u00fcfe deinen Warenkorb auf einer eigenen Seite und sende danach eine unverbindliche Reservierung an den Shopmanager.</CheckoutHint><SubmitButton text="Zum Warenkorb" onClick={goToCartReview} /></CartSummary>
            </CartSlider>
          </DetailContent>
        }
      />
      <ConfirmationDialog open={isInterestDialogOpen} title="Interesse bekunden" description={<>Danach bekommt der Kleidleswart eine E-Mail und du erhältst eine Bestätigung für <strong>{product.name}</strong>.</>} confirmText="Interesse senden" isConfirming={isSubmittingInterest} onConfirm={() => void confirmInterest()} onClose={() => setIsInterestDialogOpen(false)} />
      <ConfirmationDialog open={isDeleteDialogOpen} title="Gebrauchtartikel löschen" description={<>Soll <strong>{product.name}</strong> wirklich gelöscht werden?</>} confirmText="Artikel löschen" isConfirming={isDeletingProduct} onConfirm={() => void confirmDelete()} onClose={() => setIsDeleteDialogOpen(false)} />
    </>
  );
};
export default ProductDetail;

